import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HelpCircle, Clock, X, Bot, Ticket, History } from 'lucide-react';
import FAQChatbot from './FAQChatbot';
import MyTickets from './MyTickets';

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/middleman.secure',
  instagram: 'https://www.instagram.com/middleman.secure'
};

// Pages where floating buttons should be hidden
const ADMIN_PAGES = ['/super-admin', '/admin', '/business', '/dashboard'];

const FloatingSupportButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isMyTicketsOpen, setIsMyTicketsOpen] = useState(false);

  // Hide on admin pages
  const isAdminPage = ADMIN_PAGES.some(path => location.pathname.startsWith(path));
  if (isAdminPage) {
    return null;
  }

  const openMessenger = () => {
    window.open('https://m.me/466226189896489', '_blank');
    setIsMenuOpen(false);
  };

  const openContactForm = () => {
    navigate('/contact');
    setIsMenuOpen(false);
  };

  const openSuggestionForm = (type) => {
    // Navigate with hash to scroll to form
    navigate(`/contact?category=${type}#contact-form`);
    setIsMenuOpen(false);
    
    // Scroll to form after navigation
    setTimeout(() => {
      const form = document.getElementById('contact-form');
      if (form) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const openChatbot = () => {
    setIsChatbotOpen(true);
    setIsMenuOpen(false);
  };

  const openMyTickets = () => {
    setIsMyTicketsOpen(true);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Support Menu */}
      {isMenuOpen && (
        <div className="fixed z-40 animate-slideIn
          bottom-20 left-4 right-4 lg:bottom-auto lg:left-auto lg:right-16 lg:top-1/2 lg:-translate-y-1/2">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden w-full lg:w-72">
            {/* Header */}
            <div className="bg-[#67ffbb] text-gray-900 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Besoin d'aide ?</h3>
                  <p className="text-xs text-gray-700">Choisissez votre mode de contact</p>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-1 hover:bg-black/10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="p-3 space-y-2">
              {/* Chatbot Option */}
              <button
                onClick={openChatbot}
                className="w-full p-3 rounded-xl border-2 border-[#67ffbb]/30 hover:border-[#67ffbb] hover:bg-[#67ffbb]/10 transition-all text-left group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#67ffbb]/20 rounded-full flex items-center justify-center group-hover:bg-[#67ffbb]/30 transition-colors">
                    <Bot className="h-5 w-5 text-[#2eb382]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Chatbot FAQ</h4>
                    <p className="text-xs text-gray-500">Questions fréquentes</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-[#2eb382]" />
                      <span className="text-xs text-[#2eb382] font-medium">Réponse immédiate</span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Messenger Option */}
              <button
                onClick={openMessenger}
                className="w-full p-3 rounded-xl border-2 border-[#67ffbb]/30 hover:border-[#67ffbb] hover:bg-[#67ffbb]/10 transition-all text-left group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#67ffbb]/20 rounded-full flex items-center justify-center group-hover:bg-[#67ffbb]/30 transition-colors">
                    <svg className="h-5 w-5 text-[#2eb382]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.936 1.444 5.544 3.746 7.256v3.501l3.373-1.851c.9.25 1.851.384 2.881.384 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.093 12.441l-2.545-2.72-4.968 2.72 5.467-5.803 2.609 2.72 4.905-2.72-5.468 5.803z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Messenger</h4>
                    <p className="text-xs text-gray-500">Facebook Messenger</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-[#2eb382]" />
                      <span className="text-xs text-[#2eb382] font-medium">Moins de 1h</span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Ticket Option */}
              <button
                onClick={openContactForm}
                className="w-full p-3 rounded-xl border-2 border-[#67ffbb]/30 hover:border-[#67ffbb] hover:bg-[#67ffbb]/10 transition-all text-left group"
                data-testid="support-ticket-option"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#67ffbb]/20 rounded-full flex items-center justify-center group-hover:bg-[#67ffbb]/30 transition-colors">
                    <Ticket className="h-5 w-5 text-[#2eb382]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Ticket de support</h4>
                    <p className="text-xs text-gray-500">Suivi personnalisé</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-[#2eb382]" />
                      <span className="text-xs text-[#2eb382] font-medium">Réponse garantie</span>
                    </div>
                  </div>
                </div>
              </button>

              {/* My Tickets Option */}
              <button
                onClick={openMyTickets}
                className="w-full p-3 rounded-xl border-2 border-[#67ffbb]/30 hover:border-[#67ffbb] hover:bg-[#67ffbb]/10 transition-all text-left group"
                data-testid="my-tickets-option"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#67ffbb]/20 rounded-full flex items-center justify-center group-hover:bg-[#67ffbb]/30 transition-colors">
                    <History className="h-5 w-5 text-[#2eb382]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Mes tickets</h4>
                    <p className="text-xs text-gray-500">Voir mes demandes</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-[#2eb382]" />
                      <span className="text-xs text-[#2eb382] font-medium">Suivre mes tickets</span>
                    </div>
                  </div>
                </div>
              </button>
              
              {/* Divider */}
              <div className="border-t border-gray-200 my-2"></div>
              
              {/* Suggestion buttons - styled as actual buttons */}
              <div className="space-y-2 p-1">
                <button
                  onClick={() => openSuggestionForm('nouvelle_billeterie')}
                  className="w-full px-4 py-2.5 text-center text-sm font-medium bg-gradient-to-r from-brand-50 to-blue-50 text-brand-700 hover:from-brand-100 hover:to-blue-100 border border-brand-200 rounded-lg transition-all shadow-sm hover:shadow"
                >
                  💡 Suggérer une nouvelle billeterie
                </button>
                <button
                  onClick={() => openSuggestionForm('suggestion_amelioration')}
                  className="w-full px-4 py-2.5 text-center text-sm font-medium bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700 hover:from-purple-100 hover:to-indigo-100 border border-purple-200 rounded-lg transition-all shadow-sm hover:shadow"
                >
                  ✨ Suggérer une amélioration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop only: Vertical button on right side (hidden on mobile & tablet) */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed z-50 transition-all duration-300
          hidden lg:block
          lg:left-auto lg:right-0 lg:bottom-auto lg:top-[calc(50%-163px)]
          xl:right-0"
        style={{ right: '0px' }}
        aria-label="Support"
        data-testid="floating-support-btn"
      >
        {/* Desktop version - vertical */}
        <div
          className={`
            flex items-center gap-2 
            px-2 py-4
            h-[130px]
            ${isMenuOpen 
              ? 'bg-gradient-to-b from-gray-600 to-gray-700' 
              : 'bg-[#67ffbb] hover:bg-[#4de6a8]'}
            ${isMenuOpen ? 'text-white' : 'text-gray-900'} font-semibold text-xs lg:text-sm
            rounded-l-xl
            shadow-lg hover:shadow-xl
            transition-all duration-300
          `}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isMenuOpen ? 'bg-white' : 'bg-gray-900'} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isMenuOpen ? 'bg-white' : 'bg-gray-900'}`}></span>
          </span>
          <span className="tracking-wide">{isMenuOpen ? 'Fermer' : 'Support'}</span>
          <HelpCircle className="w-4 h-4 rotate-90" />
        </div>
      </button>

      {/* Mobile & Tablet: Horizontal button at bottom right */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed z-50 transition-all duration-300
          lg:hidden
          bottom-4 right-4"
        aria-label="Support"
        data-testid="floating-support-btn-mobile"
      >
        <div
          className={`
            flex items-center justify-center gap-2 
            px-4 py-3
            ${isMenuOpen 
              ? 'bg-gradient-to-r from-gray-600 to-gray-700' 
              : 'bg-[#67ffbb] hover:bg-[#4de6a8]'}
            ${isMenuOpen ? 'text-white' : 'text-gray-900'} font-semibold text-sm
            rounded-xl
            shadow-lg hover:shadow-xl
            transition-all duration-300
          `}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isMenuOpen ? 'bg-white' : 'bg-gray-900'} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isMenuOpen ? 'bg-white' : 'bg-gray-900'}`}></span>
          </span>
          <span className="tracking-wide">{isMenuOpen ? 'Fermer' : 'Support'}</span>
          <HelpCircle className="w-4 h-4" />
        </div>
      </button>

      {/* Chatbot */}
      <FAQChatbot 
        isOpen={isChatbotOpen} 
        onClose={() => setIsChatbotOpen(false)} 
      />

      {/* My Tickets */}
      <MyTickets
        open={isMyTicketsOpen}
        onOpenChange={setIsMyTicketsOpen}
      />

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 1024px) {
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translate(20px, -50%);
            }
            to {
              opacity: 1;
              transform: translate(0, -50%);
            }
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default FloatingSupportButton;
